import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('sac_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('sac_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  refresh: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  googleSession: (sessionId) => api.post('/auth/google-session', { session_id: sessionId }),
  googleLogin: (idToken, accountType) => api.post('/auth/google', { idToken, accountType }),
  appleLogin: (identityToken, name, accountType) => api.post('/auth/apple', { identityToken, name, accountType }),
  updateProfile: (data) => api.put('/auth/profile', data),
  checkUsername: (username) => api.get('/auth/username/check', { params: { username } }),
  sendPhoneCode: (phone) => api.post('/auth/phone/send', { phone }),
  verifyPhoneCode: (phone, code) => api.post('/auth/phone/verify', { phone, code }),
  changePassword: (data) => api.put('/auth/change-password', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  deleteAccount: (data) => api.delete('/auth/account', { data }),
};

// Events
export const eventsAPI = {
  list: (params) => api.get('/events', { params }),
  my: () => api.get('/events/my'),
  get: (id) => api.get(`/events/${id}`),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  convertToRecurring: (id, data) => api.post(`/events/${id}/recurring`, data),
  delete: (id) => api.delete(`/events/${id}`),
};

// Chat
export const chatAPI = {
  getTopics: (eventId, params) => api.get(`/chat/events/${eventId}/topics`, { params }),
  createTopic: (eventId, data) => api.post(`/chat/events/${eventId}/topics`, data),
  getReplies: (topicId, params) => api.get(`/chat/topics/${topicId}/replies`, { params }),
  addReply: (topicId, data) => api.post(`/chat/topics/${topicId}/replies`, data),
  editReply: (replyId, data) => api.put(`/chat/replies/${replyId}`, data),
  deleteReply: (replyId) => api.delete(`/chat/replies/${replyId}`),
  likeReply: (replyId) => api.post(`/chat/replies/${replyId}/like`),
  pinTopic: (topicId, data) => api.put(`/chat/topics/${topicId}/pin`, data),
  deleteTopic: (topicId) => api.delete(`/chat/topics/${topicId}`),
  myTopics: () => api.get('/chat/my'),
};

// Recurring Events
export const recurringAPI = {
  create: (data) => api.post('/recurring-events', data),
  updateSeries: (baseId, data) => api.put(`/recurring-events/${baseId}`, data),
  deleteSeries: (baseId) => api.delete(`/recurring-events/${baseId}`),
  mySeries: () => api.get('/recurring-events/my'),
  occurrences: (baseId, params) => api.get(`/recurring-events/${baseId}/occurrences`, { params }),
  updateOccurrence: (occId, data) => api.put(`/recurring-events/occurrences/${occId}`, data),
  assignDj: (baseId, data) => api.put(`/recurring-events/${baseId}/assign-dj`, data),
  seriesAnalytics: (baseId) => api.get(`/recurring-events/${baseId}/analytics`),
};

// Orders
export const ordersAPI = {
  create: (data) => api.post('/orders', data),
  checkStatus: (sessionId) => api.get(`/orders/status/${sessionId}`),
  myOrders: () => api.get('/orders/my'),
  get: (id) => api.get(`/orders/${id}`),
};

// Tickets
export const ticketsAPI = {
  myTickets: () => api.get('/tickets/my'),
  get: (id) => api.get(`/tickets/${id}`),
  getQrUrl: (id) => `${API}/tickets/${id}/qr`,
  getPdfUrl: (id) => `${API}/tickets/${id}/pdf`,
  validate: (id) => api.post(`/tickets/${id}/validate`, {}),
  secureSwap: (id, data) => api.post(`/tickets/${id}/secure-swap`, data),
};

// Organiser
export const organiserAPI = {
  events: () => api.get('/organiser/events'),
  analytics: (eventId) => api.get('/organiser/analytics', { params: eventId ? { event_id: eventId } : {} }),
  upgradeUser: (userId) => api.put(`/organiser/upgrade/${userId}`),
  payouts: () => api.get('/organiser/payout-requests'),
  createPayout: (data) => api.post('/organiser/payout-request', data),
};

// Boost (paid event visibility)
export const boostAPI = {
  plans: () => api.get('/boosts/plans'),
  create: (data) => api.post('/boosts', data),
  checkStatus: (sessionId) => api.get(`/boosts/status/${sessionId}`),
  myBoosts: () => api.get('/boosts/my'),
};

// Admin
export const adminAPI = {
  dashboard: () => api.get('/admin/dashboard'),
  users: (params) => api.get('/admin/users', { params }),
  banUser: (id) => api.post(`/admin/users/${id}/ban`),
  unbanUser: (id) => api.post(`/admin/users/${id}/unban`),
  createAdmin: (data) => api.post('/admin/users', data),
  makeDj: (id) => api.post(`/admin/users/${id}/make-dj`),
  removeDj: (id) => api.post(`/admin/users/${id}/remove-dj`),
  auditLogs: (params) => api.get('/admin/audit-logs', { params }),
  events: (params) => api.get('/admin/events', { params }),
  featureEvent: (id, featured) => api.put(`/admin/events/${id}/feature`, { is_featured: featured }),
  publishEvent: (id, published) => api.put(`/admin/events/${id}/publish`, { is_published: published }),
};

// FX
export const fxAPI = {
  rates: () => api.get('/fx/rates'),
  update: (data) => api.put('/fx/rates', data),
};

// Reviews
export const reviewsAPI = {
  list: (eventId, page = 1) => api.get(`/reviews/event/${eventId}`, { params: { page, limit: 20 } }),
  create: (data) => api.post('/reviews', data),
  reply: (reviewId, text) => api.post(`/reviews/${reviewId}/reply`, { text }),
  delete: (reviewId) => api.delete(`/reviews/${reviewId}`),
};

// Payouts
export const payoutAPI = {
  create: (data) => api.post('/organiser/payout-request', data),
  myRequests: () => api.get('/organiser/payout-requests'),
  adminList: (status) => api.get('/admin/payout-requests', { params: status ? { status } : {} }),
  approve: (id) => api.put(`/admin/payout-requests/${id}/approve`),
  reject: (id, reason) => api.put(`/admin/payout-requests/${id}/reject`, { reason }),
};

// Organizers
export const organizersAPI = {
  get: (userId) => api.get(`/organizers/${userId}`),
  contact: (userId, data) => api.post(`/organizers/${userId}/contact`, data),
  myMessages: () => api.get('/organizers/messages'),
  markMessageRead: (messageId) => api.put(`/organizers/messages/${messageId}/read`),
};

// Roles & assignments (event teams, DJ bookings, promoter campaigns)
export const assignmentsAPI = {
  eventTeam: (eventId) => api.get(`/events/${eventId}/team`),
  seriesTeam: (baseEventId) => api.get(`/series/${baseEventId}/team`),
  lineup: (eventId) => api.get(`/events/${eventId}/lineup`),
  promoters: (eventId) => api.get(`/events/${eventId}/promoters`),
  create: (data) => api.post('/assignments', data),
  update: (id, data) => api.put(`/assignments/${id}`, data),
  remove: (id) => api.delete(`/assignments/${id}`),
  mine: (params) => api.get('/assignments/my', { params }),
  accept: (id) => api.post(`/assignments/${id}/accept`),
  decline: (id) => api.post(`/assignments/${id}/decline`),
  qrBlob: (id) => api.get(`/assignments/${id}/qr`, { responseType: 'blob' }),
  invitation: (token) => api.get(`/invitations/${token}`),
  claim: (token) => api.post(`/invitations/${token}/claim`),
  searchUsers: (q, capability) => api.get('/users/search', { params: { q, capability } }),
};

export const promoterAPI = {
  dashboard: () => api.get('/promoter/dashboard'),
  requestPayout: (data) => api.post('/promoter/payout-request', data),
  trackClick: (code) => api.post(`/referrals/${code}/click`),
};

export const profilesAPI = {
  get: (userId) => api.get(`/profiles/${userId}`),
  djs: (q) => api.get('/djs', { params: { q } }),
  djDashboard: () => api.get('/dj/dashboard'),
};

export const followsAPI = {
  follow: (type, id) => api.post('/follows', { type, id }),
  unfollow: (type, id) => api.delete('/follows', { data: { type, id } }),
  status: (type, id) => api.get('/follows/status', { params: { type, id } }),
  mine: () => api.get('/follows/my'),
};

export const checkinAPI = {
  scan: (code, eventId) => api.post('/tickets/checkin/scan', { code, event_id: eventId }),
  attendees: (eventId) => api.get(`/events/${eventId}/attendees`),
};

export const adminRolesAPI = {
  setCapability: (userId, capability, action) => api.post(`/admin/users/${userId}/capabilities`, { capability, action }),
  setVerified: (userId, verified) => api.post(`/admin/users/${userId}/verify`, { verified }),
  promoters: () => api.get('/admin/promoters'),
};

// Promoter referral codes are remembered per event for 30 days so a visitor
// who arrives via a promoter link and buys later is still attributed.
const REF_KEY = 'sac_referrals';
export const referralStore = {
  set(eventId, code) {
    try {
      const all = JSON.parse(localStorage.getItem(REF_KEY) || '{}');
      all[eventId] = { code, at: Date.now() };
      localStorage.setItem(REF_KEY, JSON.stringify(all));
    } catch { /* storage unavailable */ }
  },
  get(eventId) {
    try {
      const entry = JSON.parse(localStorage.getItem(REF_KEY) || '{}')[eventId];
      return entry && Date.now() - entry.at < 30 * 24 * 3600 * 1000 ? entry.code : undefined;
    } catch {
      return undefined;
    }
  },
};

export default api;
